import React, { ReactElement, RefObject, useCallback, useState } from 'react'
import Grid from '@sport1/react-elements/Grid'
import SnapList from '@sport1/react-elements/SnapList'
import Pagination from '../../BaseComponents/Pagination'
import { TestID } from '../../utils/PropTypesDefinitions'

export type TeaserCarouselProps<T> = TestID & {
    contentWidth: number
    teaserItems: T[]
    renderElement: (item: T) => ReactElement
    keyExtractor: (item: T, index: number) => string
    hasPagination?: boolean
    onIndexChanged?: (index: number) => void
    paginationPosition?: 'below' | 'overlay'
    activeDotColor?: 'onyx' | 'pearl'
    /**
     * property for ref forwarding
     * @TJS-type React.RefObject<Element>
     * */
    innerRef?: RefObject<Element>
}

const absolutePositionProps = {
    position: 'absolute',
    bottom: 3,
    left: 0,
    right: 0,
}

const TeaserCarousel = <T,>({
    contentWidth,
    teaserItems,
    renderElement,
    keyExtractor,
    hasPagination = true,
    onIndexChanged,
    paginationPosition = 'below',
    activeDotColor = 'onyx',
    innerRef,
    testID,
}: TeaserCarouselProps<T>): ReactElement => {
    const [currentPage, setCurrentPage] = useState(1)

    const onPageChangedCallback = useCallback(
        (page: number): void => {
            if (onIndexChanged) {
                onIndexChanged(page)
            }
            setCurrentPage(page + 1)
        },
        [onIndexChanged]
    )

    const renderItem = useCallback(
        ({ item }) => ({
            component: renderElement(item),
        }),
        [renderElement]
    )

    return (
        <Grid
            noExpand
            width={contentWidth}
            position="relative"
            backgroundColor="pearl"
            ref={innerRef}
            testID={testID}
        >
            <SnapList
                data={teaserItems}
                width={contentWidth}
                renderItem={renderItem}
                keyExtractor={keyExtractor}
                onIndexChanged={onPageChangedCallback}
            />
            <Grid
                noExpand
                marginY={5}
                center
                {...(paginationPosition === 'overlay' ? absolutePositionProps : {})}
            >
                {hasPagination ? (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={teaserItems.length}
                        activeDotColor={activeDotColor}
                    />
                ) : null}
            </Grid>
        </Grid>
    )
}

export default TeaserCarousel
